/* Copyright (c) 2017 Backpack Health LLC */

import { authorizedApiRequest, externalApi } from './ApiRequest';

import FileModel from '../models/FileModel';
import ApiErrorModel from '../models/ApiErrorModel';

const FilesApi = {
  create: function createFile(profileId) {
    return authorizedApiRequest('post', `profiles/${profileId}/files`);
  },
  getUploadUrl: function getFileUploadUrl(profileId, fileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/files/${fileId}/uploadUrl`);
  },
  upload: function uploadFile(uploadUrl) {
    return externalApi('put', uploadUrl);
  },
  markCompleted: function markUploadComplete(profileId, fileId) {
    return authorizedApiRequest('put', `profiles/${profileId}/files/${fileId}/completed`);
  },
  flagged: function flagFile(profileId, fileId) {
    return authorizedApiRequest('put', `profiles/${profileId}/files/${fileId}/flagged`);
  },
  getAll: function getAllFilesForProfile(profileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/files`);
  },
  get: function getFile(profileId, fileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/files/${fileId}`);
  },
  update: function updateFile(profileId, fileId) {
    return authorizedApiRequest('put', `profiles/${profileId}/files/${fileId}`);
  },
  getDownloadUrl: function getFileDownloadUrl(profileId, fileId) {
    return authorizedApiRequest('get', `profiles/${profileId}/files/${fileId}/downloadUrl`);
  },
  del: function deleteFile(profileId, fileId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/files/${fileId}`);
  },
};

const FileService = {

  create(profileId, fileData, callback) {
    return FilesApi.create(profileId)
      .send(fileData)
      .then(res => {
        callback(null, new FileModel(res.body.data));
      })
      .catch((err) => {
        callback(new ApiErrorModel(err), null);
      });
  },

  update(profileId, fileData, callback) {
    return FilesApi.update(profileId, fileData.id)
      .send(fileData)
      .end((err) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  del(profileId, fileId, callback) {
    return FilesApi.del(profileId, fileId)
      .end((err) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  markCompleted(profileId, fileData, callback) {
    return FilesApi.markCompleted(profileId, fileData.id)
      .send({
        completed: true,
      })
      .end((err) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  flagged(profileId, fileId, isFlagged, callback) {
    return FilesApi.flagged(profileId, fileId)
    .send({
      flagged: isFlagged,
    })
    .end((err) => {
      const errInstance = err ? new ApiErrorModel(err) : null;
      callback(errInstance, null);
    });
  },

  getAll(profileId, callback) {
    return FilesApi.getAll(profileId)
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        const fileCollection = apiError ? [] : res.body.data;
        callback(apiError, fileCollection);
      });
  },

  get(profileId, fileId, callback) {
    return FilesApi.get(profileId, fileId)
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, res.body.data);
      });
  },

  getDownloadUrl(profileId, fileId, callback) {
    return FilesApi.getDownloadUrl(profileId, fileId)
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, res.body.url);
      });
  },

  getPreviewUrl(profileId, fileId, callback) {
    return FilesApi.getDownloadUrl(profileId, fileId)
      .query({ inline: true })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, res.body.url);
      });
  },

  createAndUpload(profileId, initialFileData, fileBytes, callback, progressCallback) {
    let currentRequest = FileService.create(profileId, initialFileData, (apiError1, newFile) => {
      if (apiError1) {
        callback(apiError1, null);
        return;
      }

      currentRequest = FileService.upload(profileId, newFile.data, fileBytes, (apiError2) => {
        if (apiError2) {
          callback(apiError2, null);
          return;
        }
        currentRequest = FileService.markCompleted(profileId, newFile.data, (apiError3) => {
          callback(apiError3, newFile);
        });
      }, (event) => {
        const percent = event.percent === undefined ? 0 : event.percent;
        progressCallback(percent);
      });
    });

    return {
      abort() {
        currentRequest.abort();
      },
    };
  },

  upload(profileId, fileData, fileBytes, callback, progressCallback) {
    let currentRequest = FilesApi.getUploadUrl(profileId, fileData.id);
    currentRequest.end((err1, res1) => {
      if (err1) {
        callback(new ApiErrorModel(err1), res1);
        return;
      }

      const uploadUrl = res1.body.url;
      currentRequest = FilesApi.upload(uploadUrl);
      currentRequest.set('Content-Type', fileData.mime_type)
        .send(fileBytes)
        .on('progress', progressCallback)
        .end((err2) => {
          const errInstance2 = err2 ? new ApiErrorModel(err2) : null;
          callback(errInstance2, null);
        });
    });

    return {
      abort() {
        currentRequest.abort();
      },
    };
  },
};

export default FileService;
